import { css, DefaultTheme } from 'styled-components'

/**
 * Breakpoint values in pixels (matching theme.breakpoints)
 * These are exported for direct use when theme isn't available
 */
export const BREAKPOINTS = {
  mobile: 640,
  tablet: 1024,
  desktop: 1280,
  wideDesktop: 1440,
  extraWide: 1600,
} as const

export type MediaBreakpointKey = keyof typeof BREAKPOINTS
/** @deprecated Use MediaBreakpointKey instead */
export type BreakpointKey = MediaBreakpointKey

/**
 * Media query helpers for mobile-first responsive design.
 *
 * Usage with styled-components:
 * ```tsx
 * const Component = styled.div`
 *   padding: 16px; // Mobile base
 *
 *   ${media.tablet} {
 *     padding: 24px; // Tablet and up
 *   }
 *
 *   ${media.desktop} {
 *     padding: 32px; // Desktop and up
 *   }
 * `
 * ```
 */
export const media = {
  /** Viewport >= 640px (tablet and up) */
  mobile: `@media (min-width: ${BREAKPOINTS.mobile}px)`,
  /** Viewport >= 1024px (desktop and up) */
  tablet: `@media (min-width: ${BREAKPOINTS.tablet}px)`,
  /** Viewport >= 1280px (wide desktop) */
  desktop: `@media (min-width: ${BREAKPOINTS.desktop}px)`,
  /** Viewport >= 1440px (extra wide) */
  wideDesktop: `@media (min-width: ${BREAKPOINTS.wideDesktop}px)`,
  /** Viewport >= 1600px (ultra wide) */
  extraWide: `@media (min-width: ${BREAKPOINTS.extraWide}px)`,

  // Max-width queries (for targeting specific ranges)
  /** Viewport < 640px (mobile only) */
  mobileOnly: `@media (max-width: ${BREAKPOINTS.mobile - 1}px)`,
  /** Viewport < 1024px (tablet and below) */
  tabletDown: `@media (max-width: ${BREAKPOINTS.tablet - 1}px)`,
  /** Viewport < 1280px (below desktop) */
  desktopDown: `@media (max-width: ${BREAKPOINTS.desktop - 1}px)`,

  // Feature queries
  /** Touch devices (coarse pointer) */
  touch: '@media (hover: none) and (pointer: coarse)',
  /** Non-touch devices (fine pointer) */
  pointer: '@media (hover: hover) and (pointer: fine)',
  /** User prefers reduced motion */
  reducedMotion: '@media (prefers-reduced-motion: reduce)',
  /** User prefers dark color scheme */
  darkMode: '@media (prefers-color-scheme: dark)',
  /** High contrast mode */
  highContrast: '@media (prefers-contrast: high)',
} as const

/**
 * Generate a media query using theme breakpoints.
 * Useful when you need access to theme inside the media query.
 *
 * @example
 * ```tsx
 * const Component = styled.div`
 *   ${({ theme }) => mediaUp(theme, 'tablet')} {
 *     display: flex;
 *   }
 * `
 * ```
 */
export const mediaUp = (
  theme: DefaultTheme,
  breakpoint: BreakpointKey
): string => {
  return `@media (min-width: ${theme.breakpoints[breakpoint]})`
}

/**
 * Generate a max-width media query using theme breakpoints.
 *
 * @example
 * ```tsx
 * const Component = styled.div`
 *   ${({ theme }) => mediaDown(theme, 'tablet')} {
 *     flex-direction: column;
 *   }
 * `
 * ```
 */
export const mediaDown = (
  theme: DefaultTheme,
  breakpoint: BreakpointKey
): string => {
  const value = parseInt(theme.breakpoints[breakpoint], 10)
  return `@media (max-width: ${value - 1}px)`
}

/**
 * Generate a media query for a specific range.
 *
 * @example
 * ```tsx
 * const Component = styled.div`
 *   ${({ theme }) => mediaBetween(theme, 'mobile', 'tablet')} {
 *     // Only tablet range
 *   }
 * `
 * ```
 */
export const mediaBetween = (
  theme: DefaultTheme,
  minBreakpoint: BreakpointKey,
  maxBreakpoint: BreakpointKey
): string => {
  const minValue = parseInt(theme.breakpoints[minBreakpoint], 10)
  const maxValue = parseInt(theme.breakpoints[maxBreakpoint], 10)
  return `@media (min-width: ${minValue}px) and (max-width: ${maxValue - 1}px)`
}

/**
 * Mixin for mobile-only styles (viewport < 640px)
 *
 * @example
 * ```tsx
 * const Component = styled.div`
 *   ${mobileOnly`
 *     padding: 16px;
 *     flex-direction: column;
 *   `}
 * `
 * ```
 */
export const mobileOnly = (
  strings: TemplateStringsArray,
  ...values: Array<string | number>
) => css`
  @media (max-width: ${BREAKPOINTS.mobile - 1}px) {
    ${css(strings, ...values)}
  }
`

/**
 * Mixin for touch device styles
 *
 * @example
 * ```tsx
 * const Button = styled.button`
 *   height: 32px;
 *
 *   ${touchDevice`
 *     min-height: 44px;
 *     min-width: 44px;
 *   `}
 * `
 * ```
 */
export const touchDevice = (
  strings: TemplateStringsArray,
  ...values: Array<string | number>
) => css`
  @media (hover: none) and (pointer: coarse) {
    ${css(strings, ...values)}
  }
`

/**
 * Mixin for pointer (non-touch) device styles
 *
 * @example
 * ```tsx
 * const Button = styled.button`
 *   ${pointerDevice`
 *     &:hover {
 *       transform: scale(1.05);
 *     }
 *   `}
 * `
 * ```
 */
export const pointerDevice = (
  strings: TemplateStringsArray,
  ...values: Array<string | number>
) => css`
  @media (hover: hover) and (pointer: fine) {
    ${css(strings, ...values)}
  }
`

/**
 * Utility to create responsive values that change at breakpoints.
 * Returns a function that generates CSS for a property with responsive values.
 *
 * @example
 * ```tsx
 * const responsivePadding = responsiveValue('padding', {
 *   base: '16px',
 *   mobile: '24px',
 *   tablet: '32px',
 * })
 *
 * const Component = styled.div`
 *   ${responsivePadding}
 * `
 * ```
 */
export const responsiveValue = (
  property: string,
  values: Partial<Record<'base' | BreakpointKey, string>>
) => {
  const { base, ...breakpointValues } = values

  return css`
    ${base && `${property}: ${base};`}

    ${breakpointValues.mobile &&
    css`
      ${media.mobile} {
        ${property}: ${breakpointValues.mobile};
      }
    `}

    ${breakpointValues.tablet &&
    css`
      ${media.tablet} {
        ${property}: ${breakpointValues.tablet};
      }
    `}

    ${breakpointValues.desktop &&
    css`
      ${media.desktop} {
        ${property}: ${breakpointValues.desktop};
      }
    `}

    ${breakpointValues.wideDesktop &&
    css`
      ${media.wideDesktop} {
        ${property}: ${breakpointValues.wideDesktop};
      }
    `}
  `
}

/**
 * Utility to hide element on specific breakpoints
 *
 * @example
 * ```tsx
 * const MobileOnly = styled.div`
 *   ${hideOn('desktop')}
 * `
 * ```
 */
export const hideOn = (breakpoint: BreakpointKey | 'mobile-only' | 'touch') => {
  if (breakpoint === 'mobile-only') {
    return css`
      ${media.mobile} {
        display: none;
      }
    `
  }
  if (breakpoint === 'touch') {
    return css`
      ${media.touch} {
        display: none;
      }
    `
  }
  return css`
    @media (min-width: ${BREAKPOINTS[breakpoint]}px) {
      display: none;
    }
  `
}

/**
 * Utility to show element only on specific breakpoints
 *
 * @example
 * ```tsx
 * const DesktopOnly = styled.div`
 *   ${showOn('desktop')}
 * `
 * ```
 */
export const showOn = (breakpoint: BreakpointKey | 'mobile-only' | 'touch') => {
  if (breakpoint === 'mobile-only') {
    return css`
      display: none;

      @media (max-width: ${BREAKPOINTS.mobile - 1}px) {
        display: block;
      }
    `
  }
  if (breakpoint === 'touch') {
    return css`
      display: none;

      ${media.touch} {
        display: block;
      }
    `
  }
  return css`
    display: none;

    @media (min-width: ${BREAKPOINTS[breakpoint]}px) {
      display: block;
    }
  `
}
